import { translate } from 'shared/lib/i18n.tsx';
import type { LegKickDisplayState } from '../../../domain/LegKickDisplayState.ts';
import type { RemoteDisplayState } from '../../../domain/RemoteDisplayState.ts';
import type { TrailPoint } from '../../../domain/GameState.ts';
import { defaultRemoteViewLayout, type RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import { getRemoteCalibStandZoneMetrics } from 'shared/lib/tv-game/remoteCalibStandZone.ts';
import { drawHudStrip, drawSideStrips } from '../room/letterbox.ts';
import { drawLiveSkeletonPts } from '../room/skeleton.ts';
import { drawCalibBackdrop, drawRemoteCalibrationStandZone } from '../room/calibrationShared.ts';
import { useLighterCanvasEffects, wrapCanvasText } from '../room/canvasHelpers.ts';
import { drawImageCover } from '../room/primitives.ts';

const LEG_KICK_BG = '/media/legKickBg.png';

/** P1 colour: yellow */
const P1_RGB = { r: 0, g: 210, b: 255 } as const;
/** P2 colour: blue */
const P2_RGB = { r: 255, g: 110, b: 40 } as const;

function drawLegTrails(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  bladeTrails: Map<string, TrailPoint[]>,
  gameBounds: RemoteViewLayout | undefined,
  light: boolean,
  _p1PlayerId: string | null,
  p2PlayerId: string | null,
): void {
  const now = Date.now();
  const W = canvasWidth;
  const maxFont = light ? 96 : 140;
  const minFont = light ? 56 : 72;
  const fontScale = light ? 0.045 : 0.055;
  const fontPx = Math.round(Math.min(maxFont, Math.max(minFont, W * fontScale)));
  const shadowBlur = light ? 10 : 24;

  bladeTrails.forEach((trail, key) => {
    const expireBefore = now - 250;
    const firstValid = trail.findIndex((p) => p.t > expireBefore);
    if (firstValid === -1) return;

    const tip = trail[trail.length - 1];
    if (!tip) return;

    if (gameBounds) {
      const { offsetX, offsetY, gameWidth, gameHeight } = gameBounds;
      if (
        tip.x < offsetX ||
        tip.x > offsetX + gameWidth ||
        tip.y < offsetY ||
        tip.y > offsetY + gameHeight
      ) {
        return;
      }
    }

    const isP2 = !!p2PlayerId && key.startsWith(`${p2PlayerId}_`);
    const footEmoji = isP2 ? '🔵' : '🟡';
    const shadowColor = isP2 ? 'rgba(80,120,255,0.9)' : 'rgba(255,220,0,0.9)';

    ctx.save();
    ctx.font = `${fontPx}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = shadowBlur;
    ctx.shadowColor = shadowColor;
    ctx.fillText(footEmoji, tip.x, tip.y);
    ctx.restore();
  });
}

/** Leg Kick: ankle boots only — targets, blade trails and HUD in React. */
export class LegKickCanvasLayer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly bgImage: HTMLImageElement;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2D canvas context');
    this.ctx = ctx;
    // src ставится в preload() — фон грузится только когда выбрана эта игра.
    this.bgImage = new Image();
  }

  preload(): void {
    if (!this.bgImage.src) this.bgImage.src = LEG_KICK_BG;
  }

  render(state: LegKickDisplayState): void {
    if (
      state.phase !== 'playing' &&
      state.phase !== 'gameover' &&
      state.phase !== 'calibration'
    ) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    const W = this.canvas.width;
    const H = this.canvas.height;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, W, H);
    this.drawBackground(W, H);

    if (state.phase === 'calibration') {
      this.drawCalibrationScreen(state, W, H);
      return;
    }

    const rs = state as unknown as RemoteDisplayState;
    drawHudStrip(this.ctx, rs, W);
    drawSideStrips(this.ctx, rs, H, this.canvas.width);
    const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
    if (state.splitTwoPlayers) {
      this.drawCenterUiDivider(layout);
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
    ctx.clip();

    const light = useLighterCanvasEffects(this.canvas);

    // Targets (circles)
    this.drawTargetCircles(state, layout);

    // Leg trails — P1 (🟡) and P2 (🔵)
    drawLegTrails(this.ctx, this.canvas.width, state.bladeTrails, layout, light, state.p1PlayerId, state.p2PlayerId);

    // Floating labels
    this.drawTargetLabels(state, layout);
    ctx.restore();
  }

  private drawCalibrationScreen(state: LegKickDisplayState, W: number, H: number): void {
    const single = !state.splitTwoPlayers || !state.leftView || !state.rightView;
    const light = useLighterCanvasEffects(this.canvas);

    if (single) {
      const layout = state.remoteView ?? defaultRemoteViewLayout(W, H);
      const ctx = this.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.rect(layout.offsetX, layout.offsetY, layout.gameWidth, layout.gameHeight);
      ctx.clip();
      drawCalibBackdrop(ctx, layout);
      drawLiveSkeletonPts(ctx, state.calibNormPoints, layout, false,
        P1_RGB.r, P1_RGB.g, P1_RGB.b, light);
      drawRemoteCalibrationStandZone(ctx, layout, 'square');
      this.drawDetectionProgressScan(layout, state.detectionProgress);
      this.drawCalibInstructions(layout);
      ctx.restore();
      return;
    }

    const full = state.remoteView ?? defaultRemoteViewLayout(W, H);
    const leftCalibLayout = this.buildWiderSplitCalibLayout(state.leftView!, full);
    const rightCalibLayout = this.buildWiderSplitCalibLayout(state.rightView!, full);
    const ctx = this.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.rect(full.offsetX, full.offsetY, full.gameWidth, full.gameHeight);
    ctx.clip();
    drawCalibBackdrop(ctx, full);
    // P1 skeleton + ankles (cyan), P2 skeleton + ankles (orange)
    drawLiveSkeletonPts(ctx, state.calibNormPoints, full, false,
      P1_RGB.r, P1_RGB.g, P1_RGB.b, light);
    drawLiveSkeletonPts(ctx, state.calibNormPointsP2, full, false,
      P2_RGB.r, P2_RGB.g, P2_RGB.b, light);
    this.drawCenterUiDivider(full);
    drawRemoteCalibrationStandZone(ctx, leftCalibLayout, 'square');
    this.drawDetectionProgressScan(leftCalibLayout, state.detectionProgress);
    this.drawCalibInstructions(leftCalibLayout);
    drawRemoteCalibrationStandZone(ctx, rightCalibLayout, 'square');
    this.drawDetectionProgressScan(rightCalibLayout, state.detectionProgress);
    this.drawCalibInstructions(rightCalibLayout);
    ctx.restore();
  }

  private buildWiderSplitCalibLayout(
    splitLayout: RemoteViewLayout,
    fullLayout: RemoteViewLayout,
  ): RemoteViewLayout {
    const desiredWidth = Math.min(fullLayout.gameWidth * 0.58, splitLayout.gameWidth * 1.7);
    const cx = splitLayout.offsetX + splitLayout.gameWidth / 2;
    const offsetX = cx - desiredWidth / 2;
    return { ...splitLayout, offsetX, gameWidth: desiredWidth };
  }

  private drawCenterUiDivider(layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const midX = layout.offsetX + layout.gameWidth / 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = Math.max(2, layout.gameWidth * 0.0015);
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(255,220,100,0.55)';
    ctx.setLineDash([10, 12]);
    ctx.beginPath();
    ctx.moveTo(midX, layout.offsetY + Math.max(8, layout.gameHeight * 0.02));
    ctx.lineTo(midX, layout.offsetY + layout.gameHeight - Math.max(8, layout.gameHeight * 0.02));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawDetectionProgressScan(layout: RemoteViewLayout, progress: number | null): void {
    const ctx = this.ctx;
    const p = progress == null ? 0 : Math.max(0, Math.min(1, progress));
    if (p <= 0) return;
    const { boxX, boxY, boxW, boxH, borderW } = getRemoteCalibStandZoneMetrics(layout, 'square');
    const inset = Math.max(4, borderW * 1.2);
    const innerX = boxX + inset;
    const innerY = boxY + inset;
    const innerW = boxW - 2 * inset;
    const innerH = boxH - 2 * inset;
    const fillH = innerH * p;
    const fillY = innerY + innerH - fillH;
    ctx.save();
    const g = ctx.createLinearGradient(innerX, fillY + fillH, innerX, fillY);
    g.addColorStop(0, 'rgba(25, 90, 200, 0.55)');
    g.addColorStop(0.55, 'rgba(55, 140, 255, 0.42)');
    g.addColorStop(1, 'rgba(120, 190, 255, 0.3)');
    ctx.fillStyle = g;
    ctx.fillRect(innerX, fillY, innerW, fillH);
    ctx.restore();
  }

  private drawCalibInstructions(layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { gameHeight } = layout;
    const { cx, boxX, boxY, boxW, borderW } = getRemoteCalibStandZoneMetrics(layout, 'square');
    const instrPx = Math.round(Math.min(gameHeight * 0.034, boxW / 14, 46));
    const instrPadX = boxW * 0.05;
    const sw = boxW - instrPadX * 2;
    const lineGap = instrPx * 1.45;
    const line1 = translate('calib.hint.full_body');
    const line2 = translate('calib.hint.touch_yellow_zone');
    ctx.save();
    ctx.font = `600 ${instrPx}px Arial, sans-serif`;
    const allLines = [
      ...wrapCanvasText(ctx, line1, sw),
      ...wrapCanvasText(ctx, line2, sw),
    ];
    const stripPadV = instrPx * 0.55;
    const stripH = allLines.length * lineGap + stripPadV * 2;
    const insetTop = borderW * 2.2 + Math.max(10, gameHeight * 0.015);
    const sx = boxX + instrPadX;
    const sy = boxY + insetTop;
    ctx.fillStyle = 'rgba(0,0,0,0.58)';
    ctx.beginPath();
    const stripR = Math.min(14, boxW * 0.04);
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(sx, sy, sw, stripH, stripR);
    } else {
      ctx.rect(sx, sy, sw, stripH);
    }
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowBlur = 14;
    ctx.shadowColor = 'rgba(0,0,0,0.92)';
    ctx.fillStyle = '#ffe566';
    const firstLineY = sy + stripPadV + lineGap * 0.5;
    allLines.forEach((line, i) => { ctx.fillText(line, cx, firstLineY + i * lineGap); });
    ctx.restore();
  }

  private drawTargetLabels(state: LegKickDisplayState, layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { offsetX, offsetY, gameWidth, gameHeight } = layout;
    if (state.labels.length === 0) return;
    const fontPx = Math.max(16, Math.round(gameHeight * 0.048));
    ctx.font = `900 ${fontPx}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const lb of state.labels) {
      const px = offsetX + lb.nx * gameWidth;
      const py = offsetY + lb.ny * gameHeight;
      const a = Math.max(0, Math.min(1, lb.alpha));
      if (a < 0.03) continue;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.lineWidth = Math.max(2, fontPx * 0.11);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(0,0,0,0.82)';
      ctx.fillStyle = lb.color;
      ctx.strokeText(lb.text, px, py);
      ctx.fillText(lb.text, px, py);
      ctx.restore();
    }
  }

  private drawTargetCircles(state: LegKickDisplayState, layout: RemoteViewLayout): void {
    const ctx = this.ctx;
    const { offsetX, offsetY, gameWidth, gameHeight } = layout;
    for (const t of state.targets) {
      const px = offsetX + t.nx * gameWidth;
      const py = offsetY + t.ny * gameHeight;
      const r = t.radius * gameHeight;
      const alpha = 1 - t.hitAnimationProgress;
      if (alpha < 0.01) continue;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = t.fillColor;
      ctx.fill();
      ctx.lineWidth = Math.max(2, r * 0.08);
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
      const remain = Math.max(0, 1 - t.lifetimeProgress);
      ctx.beginPath();
      ctx.arc(px, py, r + ctx.lineWidth * 0.4, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remain);
      ctx.strokeStyle = 'rgba(255,255,255,0.75)';
      ctx.lineWidth = Math.max(3, r * 0.1);
      ctx.stroke();
      if (t.showArcadeNumber && t.arcadeSequenceNumber > 0) {
        const numPx = Math.round(r * 1.1);
        ctx.font = `900 ${numPx}px 'Arial Black', Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillText(String(t.arcadeSequenceNumber), px + 1, py + 1);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(String(t.arcadeSequenceNumber), px, py);
      }
      ctx.restore();
    }
  }

  private drawBackground(W: number, H: number): void {
    const ctx = this.ctx;
    const img = this.bgImage;
    if (img.complete && img.naturalWidth > 0) {
      drawImageCover(ctx, img, 0, 0, W, H);
      return;
    }
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#050816');
    g.addColorStop(0.45, '#0a1530');
    g.addColorStop(1, '#120828');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.globalAlpha = 0.22;
    const g2 = ctx.createRadialGradient(W * 0.5, H * 0.35, 0, W * 0.5, H * 0.35, Math.max(W, H) * 0.55);
    g2.addColorStop(0, 'rgba(80, 140, 255, 0.5)');
    g2.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = g2;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
}
